import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box } from "@mui/material";
import {useCallback, useRef, useState} from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { useTranslation } from "react-i18next";
import { DEFAULT_WORKING_TIME } from "../../workingTimeConstants";
import {
  addAndEditStoreSchema,
  validateCnpj,
  validatePhoneNumber,
} from "../storeValidationSchemas";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import storeApi from "../../../../../services/api/Store";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { sharedStyles } from "../../../../../styles/shared";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import {StoreDataForm} from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/StoreDataForm";
import {AddressDataForm} from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/AddressDataForm";
import {BankingDataForm} from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/BankingDataForm";
import {
  GenericImageSelector,
  ProcessedImage
} from "@/components/general/Images/GenericImageSelector";
import {StoreWorkingScale} from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/StoreWorkingScale";

export const DEFAULT_COORDINATES: [number, number] = [-1, -1];

const AddStore = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedImages, setSelectedImages] = useState<ProcessedImage[]>([]);

  const handleImageChange = useCallback((images: ProcessedImage[]) => {
    setSelectedImages(images);
  }, []);

  const refs = {
    storeName: useRef<HTMLInputElement>(null),
    cnpj: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    description: useRef<HTMLInputElement>(null),
    street: useRef<HTMLInputElement>(null),
    number: useRef<HTMLInputElement>(null),
    district: useRef<HTMLInputElement>(null),
    city: useRef<HTMLInputElement>(null),
    state: useRef<HTMLInputElement>(null),
    bank: useRef<HTMLInputElement>(null),
    issuer: useRef<HTMLInputElement>(null),
    issuerDigit: useRef<HTMLInputElement>(null),
    accountNumber: useRef<HTMLInputElement>(null),
    bankDigit: useRef<HTMLInputElement>(null),
    pixKey: useRef<HTMLInputElement>(null),
  };

  const [cnpj, setCnpj] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const [gatewayId, setGatewayId] = useState("");
  const [accountType, setAccountType] = useState("CHECKING_ACCOUNT");
  const [pixKeyType, setPixKeyType] = useState("CPF");

  const [cep, setCep] = useState<string>("");
  const [coordinates, setCoordinates] = useState<[number, number]>(DEFAULT_COORDINATES);


  const [intervalMinutes, setIntervalMinutes] = useState<number>(60);
  const [workingHours, setWorkingHours] = useState<any>({
    timeMonday: DEFAULT_WORKING_TIME,
    timeTuesday: DEFAULT_WORKING_TIME,
    timeWednesday: DEFAULT_WORKING_TIME,
    timeThursday: DEFAULT_WORKING_TIME,
    timeFriday: DEFAULT_WORKING_TIME,
    timeSaturday: [],
    timeSunday: [],
  });

  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState("");
  const [createdStoreId, setCreatedStoreId] = useState<string | null>(null);

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(addAndEditStoreSchema) });

  const handleStoreCreation = async () => {
    setGeneralErrorMessage("");
    if (!submittedOnce) setSubmittedOnce(true);

    const isCnpjValid = validateCnpj(cnpj);
    const isPhoneValid = validatePhoneNumber(phone);

    if (isCnpjValid && isPhoneValid) {
      setButtonsDisabled(true);
      try {
        const storeData = {
          name: refs.storeName?.current?.value || "",
          email: refs.email?.current?.value || "",
          CNPJ: cnpj,
          phone: refs.phone?.current?.value || "",
          description: refs.description?.current?.value || "",
          logradouro: refs.street?.current?.value || "",
          number: refs.number?.current?.value || "",
          district: refs.district?.current?.value || "",
          city: refs.city?.current?.value || "",
          state: refs.state?.current?.value || "",
          CEP: cep,
          country: "Brazil",
          lat: coordinates[0],
          lng: coordinates[1],
          listTags: "",
          timeMonday: workingHours.timeMonday,
          timeTuesday: workingHours.timeTuesday,
          timeWednesday: workingHours.timeWednesday,
          timeThursday: workingHours.timeThursday,
          timeFriday: workingHours.timeFriday,
          timeSaturday: workingHours.timeSaturday,
          timeSunday: workingHours.timeSunday,
        };

        const accountData = {
          bank: refs.bank?.current?.value || "",
          issuer: refs.issuer?.current?.value || "",
          issuerDigit: refs.issuerDigit?.current?.value || "",
          number: refs.accountNumber?.current?.value || "",
          bankDigit: refs.bankDigit?.current?.value || "",
          accountType: accountType,
        };

        const formData = new FormData();
        formData.append("store", JSON.stringify(storeData));
        formData.append("account", JSON.stringify(accountData));
        formData.append("gatewayId", gatewayId);

        selectedImages.forEach((img, index) => {
          formData.append("images", img.resizedFull, `store_image_${index}.jpg`);
        });

        // Envia o payload completo em uma única chamada de rede
        await storeApi.createStore(formData);

        window.alert(t("storePage.management.add.successMessage", { name: refs.storeName?.current?.value }));
        navigate("/stores");
      } catch (error: any) {
        setButtonsDisabled(false);
        console.warn("Error adding store: ", error);
        setGeneralErrorMessage(error.response?.data?.message || t("storePage.management.add.unknownError"));
      }
    } else {
      if (!isCnpjValid) refs.cnpj.current?.focus();
      else if (!isPhoneValid) refs.phone.current?.focus();
    }
  };

  const renderAddStorePageContent = (
      <>
        <LeftMarginBackButton />

        <form onSubmit={onSubmit(handleStoreCreation)} style={sharedStyles.form}>
          <StoreDataForm
              register={register}
              errors={errors}
              refs={refs}
              cnpj={cnpj}
              setCnpj={setCnpj}
              phone={phone}
              setPhone={setPhone}
              submittedOnce={submittedOnce}
          />

          <AddressDataForm
              cep={cep}
              setCep={setCep}
              refs={refs}
              coordinates={coordinates}
              setCoordinates={setCoordinates}
          />

          <BankingDataForm
              refs={refs}
              accountType={accountType}
              setAccountType={setAccountType}
              pixKeyType={pixKeyType}
              setPixKeyType={setPixKeyType}
              gatewayId={gatewayId}
              setGatewayId={setGatewayId}
      />

      <GenericImageSelector
              fullWidth={800}
              fullHeight={600}
              dropzoneText="Selecione ou arraste as fotos da loja"
              onChangeImages={handleImageChange}
          />

          <StoreWorkingScale
          intervalMinutes={intervalMinutes}
          setIntervalMinutes={setIntervalMinutes}
              workingHours={workingHours}
              setWorkingHours={setWorkingHours}
       />

        {generalErrorMessage !== "" && (
            <Alert severity="error">{generalErrorMessage}</Alert>
        )}

        <Box sx={sharedStyles.dividedFormContainer}>
          <PrimaryButton
            label={t("storePage.management.add.buttons.confirm")}
            isDisabled={buttonsDisabled}
            isLoading={buttonsDisabled}
            icon={<AddBusinessIcon />}
          />
          <SecondaryButton
            label={t("storePage.management.add.buttons.cancel")}
            onClickAction={() => navigate("/stores")}
            isDisabled={buttonsDisabled}
          />
        </Box>
      </form>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderAddStorePageContent} />
    </Box>
  );
};

export default AddStore;
